import { useEffect, useMemo, useState } from "react";
import { useDeployment, useNodes, useRedeployDeployment } from "../../api/queries";
import { isConflict } from "../../api/errors";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import { Modal } from "../../components/Modal";
import { LoadingState, ErrorState } from "../../components/AsyncStates";
import { DeploymentPlacementStatusPill, DeploymentStatusPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { useEnvironmentsById } from "../environments/useEnvironmentsById";
import { formatRelativeTime, shortDigest } from "./deployFormat";
import "./services.css";

interface DeploymentDetailModalProps {
  projectId: string;
  serviceId: string;
  /** The deployment to show, or null when the modal is closed — same
   * "driven by the target" convention as `DeletePlacementDialog`. */
  deploymentId: string | null;
  canManage: boolean;
  onClose: () => void;
}

/**
 * A deployment's full detail — the timeline entry plus its per-placement
 * convergence breakdown (Host/Deploy UX Phase 2, #539). Polls (via
 * `useDeployment`) while the deployment hasn't reached a terminal status, so
 * an operator watching the active deploy sees live convergence without
 * closing and reopening this modal. From a past SUCCESSFUL (`"running"`)
 * entry, an operator who can manage the service gets "Redeploy this
 * version" behind an inline confirm (never a one-click mutation) —
 * surfacing its requires_promotion refusal as a clear, non-scary banner
 * rather than a raw 4xx (Q-7).
 *
 * Rollback deliberately does NOT live here (IA slice 4, ServiceRollout
 * board): it moved to `RollbackDialog`, the ONE rollback surface (also
 * reached from the service header and the timeline's per-row "Roll back to
 * this"), which carries the real volume-risk preview this modal never had.
 * Redeploy stays here — it is a distinct action (subject to
 * requires_promotion, replays the EXACT pinned artifact) that still makes
 * sense from within a deployment's own detail.
 */
export function DeploymentDetailModal({
  projectId,
  serviceId,
  deploymentId,
  canManage,
  onClose,
}: DeploymentDetailModalProps) {
  const { pushToast } = useToast();
  const { data: deployment, isPending, isError, error, refetch } = useDeployment(
    projectId,
    serviceId,
    deploymentId ?? undefined,
  );
  const redeployMutation = useRedeployDeployment(projectId, serviceId);
  const [confirming, setConfirming] = useState(false);

  // Which environment each placement converged IN (Host/Deploy UX Phase 4,
  // #526) — a placement outcome carries only `nodeId` (see
  // `DeploymentPlacementOutcome` in types.ts); resolving the node to its
  // environment is the same lookup `PlacementsSection` does. Bounded picker
  // fetch (limit: 100), matching that precedent — cheap relative to the modal
  // it's scoped to, and only fetched while the modal is mounted.
  const { data: nodesPage } = useNodes({ limit: 100 });
  const nodesById = useMemo(() => new Map(nodesPage?.items.map((n) => [n.id, n])), [nodesPage]);
  const environmentsById = useEnvironmentsById();

  // A fresh deployment (a different id, or the modal re-opening) starts with
  // no pending confirm and no stale mutation error from a previous one.
  useEffect(() => {
    setConfirming(false);
    redeployMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId]);

  function handleClose() {
    setConfirming(false);
    redeployMutation.reset();
    onClose();
  }

  function handleConfirm() {
    if (!deploymentId) return;
    redeployMutation.mutate(
      { deploymentId },
      {
        onSuccess: () => {
          pushToast({ title: "Redeploy started.", tone: "success" });
          handleClose();
        },
        onError: () => {
          // Inline banner below carries the server's own refusal (e.g.
          // requires_promotion) — no toast so it stays legible next to the
          // action that triggered it; leave the modal open.
          setConfirming(false);
        },
      },
    );
  }

  const canAct = canManage && deployment?.status === "running";

  return (
    <Modal
      open={deploymentId !== null}
      title={deployment?.image ? `Deployment — ${deployment.image}` : "Deployment"}
      onClose={handleClose}
      size="lg"
    >
      {isPending && <LoadingState label="Loading deployment…" />}

      {isError && (
        <ErrorState
          description={error instanceof Error ? error.message : "Couldn't load this deployment."}
          onRetry={refetch}
        />
      )}

      {deployment && (
        <div className="deployment-detail">
          {redeployMutation.isError && redeployMutation.error && (
            <Banner
              tone={isConflict(redeployMutation.error) ? "warning" : "danger"}
              title="Redeploy not available"
              className="deployment-detail__banner"
            >
              {redeployMutation.error.message || "Try again in a moment."}
            </Banner>
          )}

          <dl className="deployment-detail__summary">
            <div>
              <dt>Status</dt>
              <dd>
                <DeploymentStatusPill status={deployment.status} />
              </dd>
            </div>
            <div>
              <dt>Rolled out</dt>
              <dd className="data-table__mono">
                {deployment.convergedCount} / {deployment.targetCount}
              </dd>
            </div>
            <div>
              <dt>Digest</dt>
              <dd className="data-table__mono">
                {deployment.digest ? shortDigest(deployment.digest) : "Unpinned"}
              </dd>
            </div>
            <div>
              <dt>Strategy</dt>
              <dd>
                {deployment.strategy || "—"}
                {deployment.maxUnavailable !== undefined && ` (max unavailable: ${deployment.maxUnavailable})`}
              </dd>
            </div>
            {deployment.previousImage && (
              <div>
                <dt>Rolls back to</dt>
                <dd className="data-table__mono">{deployment.previousImage}</dd>
              </div>
            )}
            <div>
              <dt>Triggered by</dt>
              <dd>{deployment.createdBy || "—"}</dd>
            </div>
            <div>
              <dt>Started</dt>
              <dd>
                <time dateTime={deployment.createdAt} title={new Date(deployment.createdAt).toLocaleString()}>
                  {formatRelativeTime(deployment.createdAt)}
                </time>
              </dd>
            </div>
            {deployment.status !== "deploying" && deployment.status !== "pending" && (
              <div>
                <dt>Finished</dt>
                <dd>
                  <time dateTime={deployment.updatedAt} title={new Date(deployment.updatedAt).toLocaleString()}>
                    {formatRelativeTime(deployment.updatedAt)}
                  </time>
                </dd>
              </div>
            )}
            {deployment.detail && (
              <div className="deployment-detail__detail-row">
                <dt>Detail</dt>
                <dd>{deployment.detail}</dd>
              </div>
            )}
          </dl>

          <h3 className="deployment-detail__heading">Rollout by machine</h3>
          {deployment.placements.length === 0 ? (
            <p className="form-field__hint">
              {deployment.status === "deploying" || deployment.status === "pending"
                ? "Rolling out — no machine has reported yet."
                : "No per-machine outcome was recorded for this deployment."}
            </p>
          ) : (
            <ul className="deployment-detail__placements">
              {deployment.placements.map((p) => {
                // Which environment THIS placement converged in (Host/Deploy
                // UX Phase 4) — absent for a node the fleet no longer knows
                // about (deleted since this deployment ran) or one never
                // attached to an environment; render honestly, never guess.
                const node = nodesById.get(p.nodeId);
                return (
                  <li key={p.placementId} className="deployment-detail__placement-row">
                    <div className="deployment-detail__placement-top">
                      <span className="data-table__mono">{p.nodeId}</span>
                      {node?.environmentName && (
                        <EnvironmentBadge
                          name={node.environmentName}
                          envClass={environmentsById.get(node.environmentId)?.class}
                        />
                      )}
                      <DeploymentPlacementStatusPill status={p.status} />
                    </div>
                    {p.digest && <p className="data-table__mono">{shortDigest(p.digest)}</p>}
                    {p.error && (
                      <p className="deployment-detail__placement-error" role="alert">
                        {p.error}
                      </p>
                    )}
                    {p.warnings && p.warnings.length > 0 && (
                      // Warnings carry no id of their own and are never reordered — the
                      // text itself is a stable-enough key for this short, append-only list.
                      <ul className="deployment-detail__placement-warnings">
                        {p.warnings.map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {canAct && (
            <div className="deployment-detail__actions">
              {confirming ? (
                <Banner tone="warning" title="Confirm" className="deployment-detail__banner">
                  <p>Redeploy this exact version? This re-applies its pinned artifact through the normal rolling machinery.</p>
                  <div className="form-actions">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setConfirming(false)}
                      disabled={redeployMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={handleConfirm}
                      disabled={redeployMutation.isPending}
                    >
                      {redeployMutation.isPending ? "Working…" : "Confirm redeploy this version"}
                    </Button>
                  </div>
                </Banner>
              ) : (
                <div className="form-actions">
                  <Button type="button" variant="secondary" onClick={() => setConfirming(true)}>
                    Redeploy this version
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
